import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Factory, UserPlus, Loader2, Eye, EyeOff } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import api from "@/lib/api";

export default function RegisterPage() {
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/register", { username: username.trim(), password, full_name: fullName.trim() });
      toast.success("Registro enviado. Espera la aprobación de un administrador.");
      navigate("/login");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al registrarte");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background hero-gradient">
      <div className="hidden lg:flex relative">
        <img
          src="https://images.pexels.com/photos/32200451/pexels-photo-32200451.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=900"
          alt="Industrial"
          className="absolute inset-0 h-full w-full object-cover opacity-40"
        />
        <div className="relative z-10 flex flex-col justify-between p-10 w-full">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-primary/15 border border-primary/40 flex items-center justify-center">
              <Factory className="h-5 w-5 text-primary" />
            </div>
            <div>
              <div className="text-lg font-semibold tracking-tight">OT Maquila</div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Crear cuenta</div>
            </div>
          </div>
          <div className="max-w-md">
            <h2 className="text-3xl font-semibold tracking-tight">Únete a tu equipo de operaciones</h2>
            <p className="text-sm text-muted-foreground mt-2">
              Después de registrarte, un administrador deberá aprobarte y asignarte un rol antes de acceder.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-4 py-8">
        <Card className="w-full max-w-[420px] bg-card border-border">
          <CardHeader className="space-y-2">
            <CardTitle className="text-2xl font-semibold tracking-tight">Crear cuenta</CardTitle>
            <CardDescription>Llena tus datos para solicitar acceso.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-4" data-testid="register-form">
              <Alert>
                <AlertDescription className="text-xs">
                  Tu cuenta quedará <strong>pendiente</strong> de aprobación por un administrador.
                </AlertDescription>
              </Alert>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Usuario *</Label>
                <Input data-testid="register-username-input" value={username} onChange={(e) => setUsername(e.target.value)} required />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Nombre completo</Label>
                <Input data-testid="register-fullname-input" value={fullName} onChange={(e) => setFullName(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Contraseña *</Label>
                <div className="relative">
                  <Input
                    data-testid="register-password-input"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    data-testid="register-toggle-password"
                    aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Confirmar contraseña *</Label>
                <div className="relative">
                  <Input
                    data-testid="register-confirm-input"
                    type={showConfirm ? "text" : "password"}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    className="pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm((v) => !v)}
                    data-testid="register-toggle-confirm"
                    aria-label={showConfirm ? "Ocultar contraseña" : "Mostrar contraseña"}
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-colors"
                    tabIndex={-1}
                  >
                    {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <Button type="submit" data-testid="register-submit-button" disabled={loading} className="w-full h-10">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <UserPlus className="h-4 w-4 mr-2" />}
                Crear cuenta
              </Button>
              <div className="text-center text-xs text-muted-foreground">
                ¿Ya tienes cuenta?{" "}
                <Link to="/login" className="text-primary hover:underline">Inicia sesión</Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
